import * as THREE from 'three';
import { ease, harmonicNoise, normalizedTime, vec3 } from './math';
import type { CameraShot } from './types';

const FORWARD = new THREE.Vector3(0, 0, -1);

export class CinematicCamera {
  readonly camera: THREE.PerspectiveCamera;
  private readonly shots: CameraShot[];
  private readonly target = new THREE.Vector3();
  private readonly baseQuaternion = new THREE.Quaternion();
  private readonly rollQuaternion = new THREE.Quaternion();

  constructor(shots: CameraShot[], aspect = 16 / 9) {
    this.shots = [...shots].sort((a, b) => a.start - b.start);
    this.camera = new THREE.PerspectiveCamera(38, aspect, 0.05, 500);
  }

  evaluate(localTime: number, reducedMotion: boolean): CameraShot | undefined {
    const shot = this.findShot(localTime);
    if (!shot) return undefined;

    const t = ease(normalizedTime(localTime, shot.start, shot.end), shot.ease);
    this.camera.position.lerpVectors(vec3(shot.from), vec3(shot.to), t);
    this.target.lerpVectors(vec3(shot.targetFrom), vec3(shot.targetTo ?? shot.targetFrom), t);

    const focalLength = Math.max(14, shot.lens);
    this.camera.setFocalLength(focalLength);
    this.camera.updateProjectionMatrix();
    this.camera.lookAt(this.target);

    const shouldShake = (shot.rig === 'handheld' || shot.rig === 'pov') && !reducedMotion;
    if (shouldShake) {
      const amount = shot.rig === 'handheld' ? 0.025 : 0.012;
      this.camera.position.x += harmonicNoise(localTime, 11, 1.7) * amount;
      this.camera.position.y += harmonicNoise(localTime, 29, 2.1) * amount;
      this.camera.lookAt(this.target);
    }

    if (shot.roll && !reducedMotion) {
      this.baseQuaternion.copy(this.camera.quaternion);
      this.rollQuaternion.setFromAxisAngle(FORWARD, THREE.MathUtils.lerp(0, shot.roll, t));
      this.camera.quaternion.multiply(this.rollQuaternion);
    }
    return shot;
  }

  resize(width: number, height: number): void {
    this.camera.aspect = Math.max(1, width) / Math.max(1, height);
    this.camera.updateProjectionMatrix();
  }

  private findShot(time: number): CameraShot | undefined {
    if (this.shots.length === 0) return undefined;
    const first = this.shots[0]!;
    const last = this.shots[this.shots.length - 1]!;
    return (
      this.shots.find((shot, index) => {
        const isLast = index === this.shots.length - 1;
        return time >= shot.start && (time < shot.end || (isLast && time <= shot.end));
      }) ?? (time < first.start ? first : last)
    );
  }
}
